import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Fieldset } from 'primereact/fieldset';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import configSettings from '../configSettings.json';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';
import { ProgressSpinner } from 'primereact/progressspinner';

/**
 * Minimal scaffold for editing default priorities.
 * Extend this to provide actual editing UI and persistence.
 */
const DefaultPrioritiesSettings = ({ visible, onHide, columns = [], onSave }) => {
    const [isVisible, setIsVisible] = useState(visible);
    const [collapsed, setCollapsed] = useState({});
    // Track "Also apply for" selections per column
    const [applySameTo, setApplySameTo] = useState({});
    // Collapsed state for the inner "Also apply for" fieldset per column
    const [applyCollapsed, setApplyCollapsed] = useState({});
    const toastRef = useRef(null);
    const { getAccessTokenSilently } = useAuth0();
    const [token, setToken] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setIsVisible(visible);
        // Show spinner while dialog is opening and data/token prepare
        setIsLoading(!!visible);
    }, [visible]);

    const sources = useMemo(() => configSettings?.Sources || [], []);

    // defaults shape: { [column]: { [source]: "1" | "2" | ... "10" } }
    const [defaults, setDefaults] = useState({});

    const seedForColumn = () => {
        // Initial order = sources order, capped at 10
        const map = {};
        sources.forEach((s, i) => (map[s] = String(Math.min(i + 1, 10))));
        return map;
    };

    useEffect(() => {
        // Ensure we have an entry for each column shown
        setDefaults((prev) => {
            const next = { ...prev };
            (columns || []).forEach((col) => {
                if (!next[col]) next[col] = seedForColumn();
            });
            Object.keys(next).forEach((col) => {
                if (!columns.includes(col)) delete next[col];
            });
            return next;
        });
    }, [visible, columns, sources]);

    // Seed collapsed state per column (collapsed by default)
    useEffect(() => {
        setCollapsed((prev) => {
            const next = { ...prev };
            (columns || []).forEach((col) => {
                if (typeof next[col] === 'undefined') next[col] = true;
            });
            Object.keys(next).forEach((col) => {
                if (!columns.includes(col)) delete next[col];
            });
            return next;
        });
    }, [columns]);

    // Seed "Also apply for" selections and inner collapse state
    useEffect(() => {
        setApplySameTo((prev) => {
            const next = { ...prev };
            (columns || []).forEach((col) => {
                if (!next[col]) next[col] = [];
            });
            Object.keys(next).forEach((col) => {
                if (!columns.includes(col)) delete next[col];
            });
            return next;
        });
        setApplyCollapsed((prev) => {
            const next = { ...prev };
            (columns || []).forEach((col) => {
                if (typeof next[col] === 'undefined') next[col] = true; // collapsed by default
            });
            Object.keys(next).forEach((col) => {
                if (!columns.includes(col)) delete next[col];
            });
            return next;
        });
    }, [columns]);

    const updatePrioValue = (column, src, val) => {
        setDefaults((prev) => ({
            ...prev,
            [column]: {
                ...(prev[column] || {}),
                [src]: String(val),
            },
        }));
    };

    const promote = (column, src) => {
        const curr = Number(defaults?.[column]?.[src] ?? 1);
        if (curr === 1) {
            // keep 1, demote other 1s to 2
            Object.keys(defaults[column] || {}).forEach((k) => {
                if (k !== src && Number(defaults[column][k] ?? 0) === 1) updatePrioValue(column, k, '2');
            });
            updatePrioValue(column, src, '1');
            return;
        }
        const next = Math.max(1, curr - 1);
        if (next === 1) {
            Object.keys(defaults[column] || {}).forEach((k) => {
                if (k !== src && Number(defaults[column][k] ?? 0) === 1) updatePrioValue(column, k, '2');
            });
        }
        updatePrioValue(column, src, String(next));
    };

    const demote = (column, src) => {
        const curr = Number(defaults?.[column]?.[src] ?? 1);
        if (curr >= 10) return;
        updatePrioValue(column, src, String(curr + 1));
    };

    const sortedSources = (column) => {
        const map = defaults?.[column] || {};
        return Object.keys(map).sort((a, b) => {
            const av = Number(map[a] ?? 999);
            const bv = Number(map[b] ?? 999);
            if (av !== bv) return av - bv;
            return a.localeCompare(b);
        });
    };

    const resetColumn = (column) => {
        setDefaults((prev) => ({
            ...prev,
            [column]: seedForColumn(),
        }));
    };

    // Reset all columns to initial seed order (1..N by Sources)
    const resetAll = () => {
        setDefaults((prev) => {
            const next = { ...prev };
            (columns || []).forEach((col) => {
                next[col] = seedForColumn();
            });
            return next;
        });
    };

    useEffect(() => {
        const fetchToken = async () => {
            try {
                const accessToken = await fetchAccessToken(getAccessTokenSilently);
                setToken(accessToken);
            } catch (error) {
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch access token. Please try again.',
                    life: 3000
                });
                setIsLoading(false);
            }
        };
        fetchToken();
    }, [getAccessTokenSilently]);

    // Load saved default priorities from backend
    useEffect(() => {
        if (!isVisible) return;

        // If waiting for token while visible, keep spinner on
        if (!token) {
            setIsLoading(true);
            return;
        }

        const controller = new AbortController();
        let cancelled = false;
        setIsLoading(true);

        (async () => {
            try {
                const resp = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/default-priorities`,
                    {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        signal: controller.signal,
                    }
                );

                if (resp.status === 204 || resp.status === 404) return;

                if (!resp.ok) await apiCallErrorHandler(resp, 'Failed to load default priorities');

                const data = await resp.json();

                if (cancelled) return;

                setDefaults((prev) => {
                    const merged = { ...prev };
                    const srcs = sources;

                    Object.keys(data || {}).forEach((col) => {
                        const loaded = data[col] || {};
                        const m = {};
                        srcs.forEach((s, i) => {
                            const v = loaded[s];
                            m[s] = String(
                                typeof v === 'number' || typeof v === 'string'
                                    ? v
                                    : Math.min(i + 1, 10)
                            );
                        });
                        merged[col] = m;
                    });

                    return merged;
                });
            } catch (err) {
                if (err?.name === 'AbortError') return;
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Load failed',
                    detail: err?.message || 'Could not load default priorities.',
                    life: 3000,
                });
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [token, isVisible, sources]);

    // Save handler: POST to backend (like ConfigForm) and then close
    const handleSave = async () => {
        try {
            setIsSaving(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/file/default-priorities`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(defaults),
                }
            );

            if (!response.ok) await apiCallErrorHandler(response, 'Failed to save default priorities');

            toastRef.current?.show({
                severity: 'success',
                summary: 'Saved',
                detail: 'Default priorities saved.',
                life: 2000,
            });

            setTimeout(() => onHide?.(), 300);
        } catch (err) {
            toastRef.current?.show({
                severity: 'error',
                summary: 'Save failed',
                detail: err?.message || 'Could not save.',
                life: 3000,
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Helpers for "Also apply for"
    const targetColumnsFor = (col) => (columns || []).filter((c) => c !== col);

    const toggleApplyCol = (col, target, checked) => {
        setApplySameTo((prev) => {
            const set = new Set(prev[col] || []);
            if (checked) set.add(target);
            else set.delete(target);
            return { ...prev, [col]: Array.from(set) };
        });
    };

    const applyToTargets = (col) => {
        const targets = applySameTo[col] || [];
        if (!targets.length) return;
        setDefaults((prev) => {
            const next = { ...prev };
            targets.forEach((t) => {
                next[t] = { ...(prev[col] || {}) };
            });
            return next;
        });
        // Collapse inner "Also apply for"
        setApplyCollapsed((prev) => ({ ...prev, [col]: true }));
        // Clear checkboxes
        setApplySameTo((prev) => ({ ...prev, [col]: [] }));
        // Show toast
        toastRef.current?.show({
            severity: 'success',
            summary: 'Applied',
            detail: `Copied priorities to ${targets.length} column${targets.length === 1 ? '' : 's'}.`,
            life: 3000,
        });
    };

    if (!isVisible) return null;

    if (isLoading || isSaving) {
        return (
            <div style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '240px' }}>
                <Toast ref={toastRef} position="top-right" />
                <ProgressSpinner aria-label={isSaving ? 'Saving...' : 'Loading...'} />
            </div>
        );
    }

    return (
        <div style={{ padding: '0.5rem' }}>
            <Toast ref={toastRef} position="top-right" />
            <p style={{ marginBottom: '0.75rem' }}>
                Configure default priorities used for enrichment. Changes here will be applied when you choose
                “Apply Default Priorities” in the Settings menu.
            </p>

            <div style={{ maxHeight: '70vh', overflow: 'auto', borderTop: '1px solid #ddd', paddingTop: '0.5rem' }}>
                {columns.map((col) => {
                    const isColCollapsed = !!collapsed[col];
                    const summary = (sortedSources(col) || []).join(', ');
                    return (
                        <Fieldset
                            key={col}
                            toggleable
                            collapsed={isColCollapsed}
                            onToggle={(e) => setCollapsed((prev) => ({ ...prev, [col]: e.value }))}
                            legend={
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <strong style={{ minWidth: 280 }}>{col}</strong>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {isColCollapsed ? (
                                            <span style={{ color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '28rem' }}>
                                                {summary}
                                            </span>
                                        ) : null}
                                        <Button
                                            label="Reset"
                                            className="p-button-text p-button-sm"
                                            onClick={() => resetColumn(col)}
                                        />
                                    </div>
                                </div>
                            }
                            className="p-mb-3"
                        >
                            {/* Header row */}
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    fontWeight: 600,
                                    color: '#444',
                                    paddingLeft: 8,
                                    paddingRight: 8,
                                }}
                            >
                                <div style={{ width: 160 }}>Source</div>
                                <div style={{ width: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    Priority (1-10)
                                </div>
                                <div style={{ flex: 1 }} />
                            </div>

                            {/* Rows */}
                            {(sortedSources(col) || []).map((src) => {
                                const valNum = Number(defaults?.[col]?.[src] ?? 1);
                                return (
                                    <div key={src} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 8px' }}>
                                        <label style={{ width: 160 }}>{src}</label>
                                        <div
                                            style={{
                                                width: 220,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <Button
                                                icon="pi pi-arrow-up"
                                                className="p-button-sm p-button-rounded p-button-text"
                                                onClick={() => promote(col, src)}
                                                aria-label={`Promote priority for ${src}`}
                                            />
                                            {valNum < 10 ? (
                                                <Button
                                                    icon="pi pi-arrow-down"
                                                    className="p-button-sm p-button-rounded p-button-text"
                                                    onClick={() => demote(col, src)}
                                                    aria-label={`Demote priority for ${src}`}
                                                />
                                            ) : (
                                                <Button
                                                    icon="pi pi-arrow-down"
                                                    className="p-button-sm p-button-rounded p-button-text"
                                                    style={{ visibility: 'hidden' }}
                                                    aria-hidden="true"
                                                    tabIndex={-1}
                                                />
                                            )}
                                            <span
                                                style={{
                                                    width: 44,
                                                    textAlign: 'center',
                                                    fontVariantNumeric: 'tabular-nums',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {valNum}
                                            </span>
                                            {valNum < 10 ? (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-danger p-button-rounded p-button-text p-button-sm"
                                                    onClick={() => updatePrioValue(col, src, '10')}
                                                    aria-label={`Set ${src} priority to 10`}
                                                />
                                            ) : (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-danger p-button-rounded p-button-text p-button-sm"
                                                    style={{ visibility: 'hidden' }}
                                                    aria-hidden="true"
                                                    tabIndex={-1}
                                                />
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }} />
                                    </div>
                                );
                            })}

                            {/* Also apply for (collapsible, like PrioritiesSidebar) */}
                            <Fieldset
                                toggleable
                                collapsed={!!applyCollapsed[col]}
                                onToggle={(e) => setApplyCollapsed((prev) => ({ ...prev, [col]: e.value }))}
                                legend="Also apply for"
                                className="p-mt-2"
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                        gap: 8,
                                    }}
                                >
                                    {targetColumnsFor(col).map((tcol) => (
                                        <label key={tcol} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Checkbox
                                                inputId={`apply-${col}-${tcol}`}
                                                onChange={(e) => toggleApplyCol(col, tcol, e.checked)}
                                                checked={(applySameTo[col] || []).includes(tcol)}
                                            />
                                            <span>{tcol}</span>
                                        </label>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                                    <Button
                                        label="Apply"
                                        className="p-button-primary p-button-sm"
                                        onClick={() => applyToTargets(col)}
                                    />
                                </div>
                            </Fieldset>
                        </Fieldset>
                    );
                })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: 8 }}>
                <Button
                    label="Reset All"
                    className="p-button-text p-button-sm"
                    onClick={resetAll}
                    disabled={isSaving}
                />
                <Button
                    label={isSaving ? 'Saving…' : 'Save'}
                    className="p-button-primary p-button-sm"
                    onClick={handleSave}
                    disabled={isSaving}
                />
            </div>
        </div>
    );
};

export default DefaultPrioritiesSettings;